import * as React from 'react';
import { connect } from 'react-redux';
import { push } from 'react-router-redux';
import * as moment from 'moment';
import * as queryString from 'query-string';
import { assign, sortBy } from 'lodash';
import {
  ButtonGroup,
  IconButton,
  Notification,
  Form,
  BookingItem,
  RouteWrapper,
  StaggeredList,
  Permission,
  ReservationItem,
  Loader,
  Section,
  Content,
  HeadingSection,
  HolidayItem,
  Pagination,
} from '../../components';
import { getDay } from '../../redux/actions/history';
import RESERVATION_TYPES from '../../data/reservationTypesFilter';
import PAGINATE from '../../data/paginate';

interface Props {
  history: any;
  vehicles: Array<any>;
  vehicleTypes: Array<any>;
  dispatch: Function;
  match: any;
}

interface State {
  date: any;
  events: Array<any>;
  ready: boolean;
  search: string;
  typeFilter?: number;
  reservationTypeFilter?: string;
  page: number;
}

class Route extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      date: moment(this.props.match.params.date),
      events: [],
      ready: false,
      search: '',
      page: 0,
    };
  }

  setDate(date) {
    this.setState({ date: moment(date), events: [] }, async () => {
      if (!this.state.date.isValid()) {
        this.props.dispatch(push('/history'));
      } else {
        const response = await this.props.dispatch(
          getDay(
            this.state.date.year(),
            this.state.date.month(),
            this.state.date.date(),
          ),
        );

        const events = [];

        response.holidays.forEach((holiday) => {
          events.push(
            assign(holiday, {
              eventType: 'holiday',
              length: moment(holiday.end).diff(holiday.start, 'days'),
            }),
          );
        });

        response.reservations.forEach((reservation) => {
          events.push(
            assign(reservation, {
              eventType: 'reservation',
              length: moment(reservation.end).diff(reservation.start, 'days'),
            }),
          );
        });

        response.bookings.forEach((booking) => {
          events.push(
            assign(booking, {
              eventType: 'booking',
              length: moment(booking.end).diff(booking.start, 'days'),
            }),
          );
        });

        this.setState({
          events: [
            ...response.holidays,
            ...response.reservations,
            ...sortBy(response.bookings, 'start'),
          ],
        });
      }
    });
  }

  async componentDidMount() {
    this.setDate(this.props.match.params.date);
    this.setState({ ready: true });
  }

  renderEvent(event) {
    switch (event.eventType) {
      case 'booking':
        return (
          <BookingItem
            key={event.id}
            booking={event}
            currentDay={this.state.date}
            hideControls
            link={`/bookings/${event.id}`}
          />
        );
      case 'reservation':
        return (
          <ReservationItem
            key={event.id}
            reservation={event}
            currentDay={this.state.date}
            link={`/reservations/${event.id}`}
          />
        );
      case 'holiday':
        return (
          <HolidayItem
            key={event.id}
            holiday={event}
            currentDay={this.state.date}
          />
        );
    }
  }

  private renderVehicleTypeSelector = () => {
    return (
      <div className="mb">
        <Form
          key="vehicle_types"
          type="form"
          fields={[
            {
              name: 'vehicle_type',
              type: 'select',
              placeholder: 'Filter...',
            },
          ]}
          values={{
            vehicle_type: RESERVATION_TYPES.concat(
              this.props.vehicleTypes.map((vehicleType) => ({
                key: vehicleType.id,
                name: vehicleType.name,
                value: vehicleType.id,
                subOption: true,
              })),
            ),
          }}
          disabled={!this.state.ready}
          hasButton={false}
          handleChange={({ vehicle_type }) => {
            this.setState({
              typeFilter: Number(vehicle_type)
                ? vehicle_type === 'Filter...'
                  ? undefined
                  : Number(vehicle_type)
                : null,
              reservationTypeFilter: !Number(vehicle_type)
                ? vehicle_type === 'Filter...'
                  ? undefined
                  : vehicle_type
                : null,
              page: 0,
            });
          }}
        />
      </div>
    );
  };

  render() {
    const { period } = this.props.match.params;

    let events = this.state.events;

    if (this.state.typeFilter) {
      events = events.filter(
        (event) => event.vehicle_type_id === this.state.typeFilter,
      );
    }

    if (this.state.reservationTypeFilter) {
      const eventType = this.state.reservationTypeFilter.toLocaleLowerCase();
      eventType === 'booking'
        ? (events = events.filter((event) => event.eventType === eventType))
        : (events = events.filter(
            (event) => event.type === this.state.reservationTypeFilter,
          ));
    }

    if (this.state.search) {
      events = events.filter(
        (event) =>
          (event.contact_name &&
            event.contact_name.toLowerCase().indexOf(this.state.search) !==
              -1) ||
          (event.vehicle &&
            event.vehicle.registration
              .toLowerCase()
              .indexOf(this.state.search) !== -1),
      );
    }

    return (
      <RouteWrapper
        title={`History for ${this.state.date.format('DD/MM/YY')}`}
        requirePermission="MANAGE_BOOKINGS"
        requireAuth
      >
        <HeadingSection
          small="History for"
          title={this.state.date.format('dddd Do MMMM YYYY')}
          controls={[
            <Permission require="MANAGE_RESERVATIONS">
              <IconButton
                icon="plus"
                className="is-primary is-outlined"
                link={`/reservations/create?${queryString.stringify({
                  start_date:
                    this.state.date !== null &&
                    this.state.date.format('YYYY-MM-DD'),
                  end_date:
                    this.state.date !== null &&
                    this.state.date.format('YYYY-MM-DD'),
                })}`}
              >
                Reserve a Vehicle
              </IconButton>
            </Permission>,
            <IconButton
              icon="undo"
              className="is-default"
              handleClick={() => {
                this.props.dispatch(push(`/history/${period}`));
              }}
            >
              Return
            </IconButton>,
          ]}
        />
        <div className="container">
          {!this.props.history.isFetching && this.state.ready && (
            <div className="is-pulled-right">
              <ButtonGroup>
                <Form
                  type="search"
                  fields={[{ name: 'search', type: 'text' }]}
                  hasButton={false}
                  handleChange={({ search }) => {
                    this.setState({ search: search.toLowerCase() });
                  }}
                />
                {this.renderVehicleTypeSelector()}
              </ButtonGroup>
            </div>
          )}
          <ButtonGroup>
            <IconButton
              icon="chevron-left"
              className="is-default"
              handleClick={() => {
                const date = this.state.date.clone().subtract(1, 'days');
                this.props.dispatch(
                  push(`/history/${period}/${date.format('YYYY-MM-DD')}`),
                );
                this.setDate(date);
              }}
            >
              Previous Day
            </IconButton>
            <IconButton
              icon="chevron-right"
              className="is-default"
              isRight
              handleClick={() => {
                const date = this.state.date.clone().add(1, 'days');
                this.props.dispatch(
                  push(`/history/${period}/${date.format('YYYY-MM-DD')}`),
                );
                this.setDate(date);
              }}
            >
              Next Day
            </IconButton>
          </ButtonGroup>
        </div>
        {this.props.history.isFetching || !this.state.ready ? (
          <Section>
            <Loader fullwidth />
          </Section>
        ) : (
          <Section>
            <Content>
              {events.length === 0 && (
                <Notification type="default">
                  No events for this date
                </Notification>
              )}
              <StaggeredList>
                {events
                  .slice(
                    this.state.page * PAGINATE.historyCalendarEvents.MAX,
                    (this.state.page + 1) * PAGINATE.historyCalendarEvents.MAX,
                  )
                  .map((event) => this.renderEvent(event))}
              </StaggeredList>
              <Pagination
                page={this.state.page}
                lastPage={
                  events.slice(
                    this.state.page * PAGINATE.historyCalendarEvents.MAX,
                    (this.state.page + 1) *
                      (PAGINATE.historyCalendarEvents.MAX + 1),
                  ).length <= PAGINATE.historyCalendarEvents.MAX
                }
                handleUpdate={async (page) => {
                  this.setState({ page });
                  events
                    .slice(
                      this.state.page * PAGINATE.historyCalendarEvents.MAX,
                      (this.state.page + 1) *
                        PAGINATE.historyCalendarEvents.MAX,
                    )
                    .map((event) => this.renderEvent(event));
                }}
              />
            </Content>
          </Section>
        )}
      </RouteWrapper>
    );
  }
}

const mapStateToProps = ({ history, vehicles, vehicleTypes }) => ({
  history,
  vehicles: vehicles.isFetching ? [] : vehicles.vehicles,
  vehicleTypes: vehicleTypes.isFetching ? [] : vehicleTypes.vehicleTypes,
});

export default connect(mapStateToProps)(Route);
